import { type ReactNode } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuPlus as Plus } from 'react-icons/lu'
import type { DataTableColors } from './DataTable'
import { ACCENT, EASE_OUT, FONT_DISPLAY, PRIMARY } from './tokens'

export interface PageHeaderProps {
  title: string
  subtitle?: string
  buttonLabel?: string
  buttonIcon?: ReactNode
  onButtonClick?: () => void
  colors: DataTableColors
  buttonBg?: string
}

/**
 * Encabezado de pantalla. La veta de color a la izquierda del título es el mineral
 * del módulo (`--k-accent`): al cambiar de sección cambia de color, y entra
 * "trazándose" — es el único adorno y marca dónde estás.
 */
export function PageHeader({
  title,
  subtitle,
  buttonLabel,
  buttonIcon,
  onButtonClick,
  colors,
  buttonBg = PRIMARY,
}: PageHeaderProps) {
  return (
    <Flex justifyContent="space-between" alignItems={{ base: 'flex-start', md: 'center' }} gap={4} direction={{ base: 'column', md: 'row' }}>
      <Flex gap={4} align="stretch">
        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.5, ease: EASE_OUT }}
          style={{ width: 5, borderRadius: 5, background: `linear-gradient(180deg, ${ACCENT}, transparent)`, transformOrigin: 'top', flexShrink: 0 }}
        />
        <Box>
          <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, ease: EASE_OUT }}>
            <Text as="h1" fontFamily={FONT_DISPLAY} fontSize={{ base: '26px', md: '32px' }} fontWeight="800" color={colors.text} letterSpacing="-0.035em" lineHeight="1.05">
              {title}
            </Text>
          </motion.div>
          {subtitle && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12, duration: 0.35 }}>
              <Text fontSize="14.5px" color={colors.textMuted} mt={1.5}>{subtitle}</Text>
            </motion.div>
          )}
        </Box>
      </Flex>
      {buttonLabel && onButtonClick && (
        <motion.button
          type="button"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -2, boxShadow: '0 14px 26px -12px rgba(180,83,42,0.55)' }}
          whileTap={{ scale: 0.97, y: 0 }}
          onClick={onButtonClick}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 9, height: 44, padding: '0 20px', border: 0, borderRadius: 13,
            background: buttonBg, color: '#fff', fontWeight: 650, fontSize: 14.5, cursor: 'pointer', whiteSpace: 'nowrap',
            boxShadow: '0 8px 18px -10px rgba(180,83,42,0.45), inset 0 1px 0 rgba(255,255,255,0.12)', flexShrink: 0,
          }}
        >
          <motion.span whileHover={{ rotate: 90 }} style={{ display: 'flex' }}>
            {buttonIcon ?? <Plus size={18} />}
          </motion.span>
          {buttonLabel}
        </motion.button>
      )}
    </Flex>
  )
}
